import React, { useState } from 'react';
import { Task, ActivityType } from '../types';
import { CreateTimeEntryRequest } from '../services/time';

const ACTIVITY_TYPES: { value: ActivityType; label: string }[] = [
  { value: 'meeting', label: 'Meeting' },
  { value: 'phone-call', label: 'Phone Call' },
  { value: 'email', label: 'Email' },
  { value: 'document-review', label: 'Document Review' },
  { value: 'research', label: 'Research' },
  { value: 'travel', label: 'Travel' },
  { value: 'court', label: 'Court' },
  { value: 'other', label: 'Other' },
];

export interface TimeEntryModalProps {
  task: Task;
  onSubmit: (timeEntry: CreateTimeEntryRequest) => Promise<void>;
  onSkip: () => void;
  onCancel?: () => void;
}

/**
 * Modal for logging time after completing a task.
 * Accepts task prop with caseId for time entry association.
 * 
 * Requirements: 4.2, 4.3, 4.4
 */
export const TimeEntryModal: React.FC<TimeEntryModalProps> = ({ task, onSubmit, onSkip, onCancel }) => {
  const [form, setForm] = useState<CreateTimeEntryRequest>({
    caseId: task.caseId,
    date: new Date().toISOString().split('T')[0],
    duration: 15,
    activityType: 'other',
    description: `Completed: ${task.title}`,
    billable: true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await onSubmit(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%'
      }}>
        <h3 style={{ marginTop: 0 }}>Log Time for Completed Task?</h3>
        <p style={{ color: '#666', marginBottom: '16px' }}>
          Task "{task.title}" marked complete. Would you like to log time spent?
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Date</label>
              <input
                type="date"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Duration</label>
              <select
                value={form.duration}
                onChange={e => setForm({ ...form, duration: Number(e.target.value) })}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                {[15, 30, 45, 60, 90, 120, 180, 240].map(d => (
                  <option key={d} value={d}>{d} min</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Activity Type</label>
            <select
              value={form.activityType}
              onChange={e => setForm({ ...form, activityType: e.target.value as ActivityType })}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              {ACTIVITY_TYPES.map(a => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Description</label>
            <input
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              required
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <input
              type="checkbox"
              checked={form.billable}
              onChange={e => setForm({ ...form, billable: e.target.checked })}
              id="billable"
            />
            <label htmlFor="billable">Billable</label>
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #f44336',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  backgroundColor: 'white',
                  color: '#f44336'
                }}
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={onSkip}
              disabled={saving}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: saving ? 'not-allowed' : 'pointer',
                backgroundColor: 'white'
              }}
            >
              Skip
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {saving ? 'Saving...' : 'Log Time'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Creates a time entry request from a task.
 * Ensures the caseId from the task is used for the time entry.
 * 
 * Property 8: Time entry uses correct caseId
 * Validates: Requirements 4.3
 */
export function createTimeEntryFromTask(
  task: Task,
  overrides?: Partial<Omit<CreateTimeEntryRequest, 'caseId'>>
): CreateTimeEntryRequest {
  return {
    caseId: task.caseId,
    date: overrides?.date ?? new Date().toISOString().split('T')[0],
    duration: overrides?.duration ?? 15,
    activityType: overrides?.activityType ?? 'other',
    description: overrides?.description ?? `Completed: ${task.title}`,
    billable: overrides?.billable ?? true,
  };
}
